import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Loader } from '@shared/ui/Progress';
import { fmtDate, RESPONSE_TEMPLATE_TYPES, statusBadge } from './constants';
import {
  ActiveBox,
  ActiveTemplateHeader,
  Bar,
  BtnRow,
  EmptySlot,
  FileName,
  FilterLabel,
  Note,
  OtherVersions,
  Slot,
  SlotGrid,
  SlotHead,
  SlotTitle,
  TplMeta,
  TplName,
  VersionActionGroup,
  VersionRow,
} from './styles';
import type { OutgoingTemplatesViewProps } from './types';

/// Вкладка «Шаблоны исходящих»: 4 слота по типам. Каждый слот показывает
/// один включённый шаблон (эталон для генерации), остальные версии — свёрнуты.
/// Наглядно доносит инвариант «один тип — один активный шаблон».
export function OutgoingTemplatesView({
  items,
  isLoading,
  canWrite,
  onUpload,
  onDownload,
  onArchive,
  onDetails,
  onToggle,
}: OutgoingTemplatesViewProps) {
  return (
    <>
      <Note>
        Эталоны для ОСНОВА.Шаблонизатор. На каждый тип исходящего документа — <strong>один
        включённый</strong> шаблон: именно его агент берёт за основу при генерации и переписывает
        тело под факты. При загрузке нового шаблона в занятый тип он автоматически станет активным
        вместо текущего.
      </Note>

      <Bar>
        <FilterLabel>
          Всего шаблонов: <strong>{items.length}</strong>
        </FilterLabel>
        <Button
          onClick={onUpload}
          disabled={!canWrite}
          title={!canWrite ? 'Только чтение' : undefined}
        >
          Загрузить шаблон
        </Button>
      </Bar>

      {isLoading ? (
        <Loader />
      ) : (
        <SlotGrid>
          {RESPONSE_TEMPLATE_TYPES.map((type) => {
            const group = items.filter((d) => d.document_type === type.value);
            const enabled = group.find((d) => d.is_enabled);
            const others = group.filter((d) => d.id !== enabled?.id);

            return (
              <Slot key={type.value}>
                <SlotHead>
                  <SlotTitle>{type.label}</SlotTitle>
                  <Badge $tone="neutral">{group.length} шт.</Badge>
                </SlotHead>

                {enabled ? (
                  <ActiveBox>
                    <ActiveTemplateHeader>
                      <TplName title={enabled.title}>{enabled.title}</TplName>
                      <Badge $tone="success">Активен для генерации</Badge>
                    </ActiveTemplateHeader>
                    <TplMeta>
                      Загружен {fmtDate(enabled.uploaded_at)}
                      {enabled.status !== 'active' && <> · {statusBadge(enabled.status)}</>}
                    </TplMeta>
                    <BtnRow>
                      <Button
                        $variant="secondary"
                        $size="sm"
                        disabled={enabled.status !== 'active'}
                        title={
                          enabled.status === 'active'
                            ? 'Показать извлечённые требования'
                            : 'Доступно после индексации'
                        }
                        onClick={() => onDetails(enabled)}
                      >
                        Подробнее
                      </Button>
                      <Button
                        $variant="secondary"
                        $size="sm"
                        title="Скачать исходный DOCX шаблона"
                        onClick={() => onDownload(enabled.id, enabled.title)}
                      >
                        Скачать
                      </Button>
                      <Button
                        $variant="ghost"
                        $size="sm"
                        onClick={() => onArchive(enabled.id)}
                        disabled={!canWrite}
                        title={!canWrite ? 'Только чтение' : undefined}
                      >
                        Архив
                      </Button>
                    </BtnRow>
                  </ActiveBox>
                ) : (
                  <EmptySlot>
                    Нет активного шаблона — загрузите, чтобы агент мог генерировать этот документ.
                  </EmptySlot>
                )}

                {others.length > 0 && (
                  <OtherVersions>
                    <summary>Другие версии ({others.length})</summary>
                    {others.map((d) => (
                      <VersionRow key={d.id}>
                        <FileName title={d.title}>{d.title}</FileName>
                        <VersionActionGroup>
                          <Button
                            $variant="secondary"
                            $size="sm"
                            disabled={d.status !== 'active' || !canWrite}
                            title={
                              d.status !== 'active'
                                ? 'Доступно после индексации'
                                : !canWrite
                                  ? 'Только чтение'
                                  : 'Сделать активным для генерации'
                            }
                            onClick={() => onToggle(d.id)}
                          >
                            Сделать активным
                          </Button>
                          <Button
                            $variant="secondary"
                            $size="sm"
                            title="Скачать исходный DOCX шаблона"
                            onClick={() => onDownload(d.id, d.title)}
                          >
                            Скачать
                          </Button>
                          <Button
                            $variant="ghost"
                            $size="sm"
                            onClick={() => onArchive(d.id)}
                            disabled={!canWrite}
                            title={!canWrite ? 'Только чтение' : undefined}
                          >
                            Архив
                          </Button>
                        </VersionActionGroup>
                      </VersionRow>
                    ))}
                  </OtherVersions>
                )}
              </Slot>
            );
          })}
        </SlotGrid>
      )}
    </>
  );
}
